import classNames from "classnames";
import { useComponentState } from "../../utils/customHook.util";
import Button from "../Common/Buttons";
import { Flex, FlexInline } from "../Common/Flex";
import Icon from "../Common/Icon";
import Tag from "../Common/Tag";
import "./SelectTags.css";

export type TagSelection<T extends string> = {
    id: T;
    value: string;
    displayValue?: string | React.JSX.Element;
    selected: boolean;
};

type Props<T extends string> = {
    className?: string;
    values: TagSelection<T>[];
    selectMultiple?: boolean;
    size?: "small" | "medium";
    disabled?: boolean;
    onChange: (newValues: TagSelection<T>[]) => void;
};

type State<T extends string> = {
    selections: TagSelection<T>[];
};

function SelectTags<T extends string>(props: Props<T>) {
    const {
        className,
        values,
        onChange,
        selectMultiple = false,
        size = "small",
        disabled = false,
    } = props;
    const { state, setState } = useComponentState<State<T>>({
        selections: values || [],
    });

    const TagComponent = size === "medium" ? Tag.Medium.Bold : Tag.Small.Bold;

    function setNewValues(tag: TagSelection<T>) {
        const newSelections = selectMultiple
            ? state.selections.map((it) => (it.id === tag.id ? tag : it))
            : state.selections.map((it) =>
                  it.id === tag.id ? tag : { ...it, selected: false },
              );
        setState({
            selections: newSelections,
        });
        onChange(newSelections);
    }

    return (
        <Flex.Row gap="small" className={classNames("select-tags", className)}>
            {state.selections.map((tag) => {
                const TagComponentSelected = tag.selected
                    ? TagComponent.P90
                    : TagComponent.N80;

                return (
                    <Button.Ghost
                        key={tag.id}
                        onClick={() => {
                            setNewValues({
                                ...tag,
                                selected: !tag.selected,
                            });
                        }}
                        disabled={disabled}
                    >
                        <TagComponentSelected>
                            <FlexInline>
                                {tag.selected && <Icon.Check.XSmall.P10 />}
                                {tag.displayValue || tag.value}
                            </FlexInline>
                        </TagComponentSelected>
                    </Button.Ghost>
                );
            })}
        </Flex.Row>
    );
}

export default SelectTags;
